import React from 'react';
import { Link } from 'react-router-dom';
import { divisionMappings, mapMappings } from '../../Constants';
import TeamLogo from '../../components/TeamLogo';
import { UpcomingMatch } from '../../models/UpcomingMatch';
import { parseMatchTime } from '../../utils/matchTime';
import { LiveMatch, currentRound, orientToTeam1 } from '../../services/LiveService';
import LiveBadge from '../LivePage/LiveBadge';
import styles from './PreMatch2.module.css';

/** "om 2t 14m" / "om 3 dage" / "starter nu" — the countdown that replaces the score. */
export const countdownTo = (kickoff: Date, now: Date = new Date()): string => {
    const ms = kickoff.getTime() - now.getTime();
    if (ms <= 0) return 'startet';
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `om ${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `om ${hours}t ${mins % 60}m`;
    const days = Math.floor(hours / 24);
    return days === 1 ? 'i morgen' : `om ${days} dage`;
};

interface HeaderProps {
    match: UpcomingMatch;
    /** The prep page shows a back link instead of the tab row, so it renders the header alone. */
    format?: string;
    /** The map being played right now, when the live feed has it. Swaps the kickoff time for the
     *  running score; the page owns the subscription so it can also decide where to redirect. */
    live?: LiveMatch | null;
}

/**
 * The match header in its pre-match state: identical geometry to Match2Header, with the score
 * slot carrying the kickoff time instead of a result.
 */
export const PreMatch2Header: React.FC<HeaderProps> = ({ match, format, live }) => {
    const kickoff = parseMatchTime(match.match_time);
    const meta = [
        divisionMappings.get(match.division) ?? match.division,
        `Sæson ${match.team1?.season ?? ''}`.trim(),
        kickoff.toLocaleDateString('da-DK').replaceAll('.', '/'),
    ].filter(Boolean).join(' · ');

    const time = kickoff.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    // The feed may list the teams in the other order; the header's left side is always team1.
    const [liveLeft, liveRight] = live ? orientToTeam1(live, match.team1.teamId) : [null, null];

    return (
        <div className={styles.header}>
            <Link to={`/team/${match.team1.teamId}`} className={styles.headerTeam}>
                <TeamLogo
                    logo={match.team1.teamLogo}
                    name={match.team1.teamName}
                    teamId={match.team1.teamId}
                    className={styles.headerLogo}
                />
                <span className={styles.headerTeamName}>{match.team1.teamName}</span>
            </Link>
            <div className={styles.headerCenter}>
                <div className={styles.headerAccent} />
                <div className={styles.headerMeta}>{meta}</div>
                {live && liveLeft && liveRight ? (
                    <>
                        <div className={styles.headerScore}>
                            <span className={liveLeft.score > liveRight.score ? styles.scoreWin : undefined}>{liveLeft.score}</span>
                            <span className={styles.scoreColon}>:</span>
                            <span className={liveRight.score > liveLeft.score ? styles.scoreWin : undefined}>{liveRight.score}</span>
                        </div>
                        <div className={`${styles.headerFormat} ${styles.headerStatus}`}>
                            <LiveBadge finished={live.finished} />
                            <span>
                                {[
                                    live.map ? (mapMappings.get(live.map) ?? live.map.replace(/^de_/, '')) : null,
                                    `Runde ${currentRound(live)}`,
                                ].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                        <Link to={`/live/${match.challonge_id}`} className={styles.liveLink}>Live scoreboard →</Link>
                    </>
                ) : (
                    <>
                        <div className={styles.headerKickoff}>{time}</div>
                        <div className={styles.headerFormat}>
                            {format ? `${format} · ` : ''}{countdownTo(kickoff)}
                        </div>
                    </>
                )}
            </div>
            <Link
                to={`/team/${match.team2.teamId}`}
                className={`${styles.headerTeam} ${styles.headerTeamRight}`}
            >
                <span className={styles.headerTeamName}>{match.team2.teamName}</span>
                <TeamLogo
                    logo={match.team2.teamLogo}
                    name={match.team2.teamName}
                    teamId={match.team2.teamId}
                    className={styles.headerLogo}
                />
            </Link>
        </div>
    );
};

/** Percentage helper that distinguishes "0%" from "no data at all". */
export const pct = (won: number, total: number): number | null =>
    total > 0 ? Math.round((won / total) * 100) : null;
